import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { LuCheck } from 'react-icons/lu';
import { functions } from '../firebase/Init';
import useEntitlement from '../hooks/useEntitlement';
import { useAuth } from '../contexts/AuthContext';
import { COPY } from '../contracts/copyMap';
import { ROUTES } from '../contracts/routes';
import { ENTITLEMENT_STATUS } from '../contracts/billingApi';
import formatMembershipDate, { formatMemberSince } from '../lib/membershipDate';
import AppLayout from '../components/oracle/AppLayout';
import HeaderTitle from '../components/oracle/HeaderTitle';
import BackButton from '../components/oracle/BackButton';
import CensorBar from '../components/oracle/CensorBar';
import AurumButton from '../components/oracle/AurumButton';
import BoneButton from '../components/oracle/BoneButton';
import MembershipCTA from '../components/billing/MembershipCTA';
import TrialBadge from '../components/billing/TrialBadge';
import { portalReturnUrls } from '../lib/billingReturnUrls';
import CancelMembershipModal from '../components/billing/CancelMembershipModal';
import MembershipCelebration from '../components/billing/MembershipCelebration';
import LogoSpinner from '../components/oracle/LogoSpinner';
import GhostButton from '../components/oracle/GhostButton';
import { track } from '../analytics/track';
import { CONVERSION_EVENTS, useTrackViewOnce } from '../analytics/conversion';

// /membership — status + self-service, and the two Stripe return landings.
//
// `mode` selects which of the three this render is:
//   'status'  -> /membership
//   'success' -> /membership/success
//   'cancel'  -> /membership/cancel
//
// ── WHY SUCCESS POLLS ───────────────────────────────────────────────────────
//
// Stripe redirects the user back the instant checkout completes, but the
// webhook that actually grants the entitlement is a separate delivery that may
// land a second or two later — or, on a retry, considerably later.
//
// So the success page NEVER trusts the fact that it was reached. It reads the
// live entitlement doc and shows a PROCESSING state until membership actually
// appears. Trusting the redirect would mean a query parameter grants access,
// which is a paywall anyone can walk through by typing a URL.
//
// useEntitlement is already an onSnapshot, so the webhook landing updates this
// page on its own. The poll below is a belt-and-braces callable read for the
// case where the snapshot listener is blocked or slow, and it gives up after
// POLL_WINDOW_MS rather than spinning forever.

const POLL_INTERVAL_MS = 2000;
const POLL_WINDOW_MS = 30000;

// Long form here, short form on Account's MembershipCard — same helper, same
// null handling, one place to change how a renewal date reads.
const formatDate = (value) => formatMembershipDate(value, { long: true });

// Page shell.
//
// NOT PageHeader — that primitive's own note says a page needing a back button
// should compose its own header rather than widening it, which is what
// VaultEntry does. Same composition here: control pinned left, title centred
// on the line, at both breakpoints.
//
// The back button is a destination (/account), not history.back(). Stripe
// redirects land on /membership/success and /membership/cancel from an
// off-origin page, so "back" there would mean Stripe.
const Shell = ({ children }) => (
  <AppLayout
    header={(
      <div className="relative flex items-center justify-center">
        <BackButton
          to={ROUTES.ACCOUNT}
          label={COPY.MEMBERSHIP_BACK_LABEL}
          className="absolute left-0 top-1/2 -translate-y-1/2"
        />
        {/* mx-14 reserves both gutters so the centred title clears the 44px
            control pinned left — without it the M sits under the arrow on a
            375px phone. MARGIN, not padding: `pl-[0.3em]` (the tracking-offset
            trick that optically re-centres letter-spaced type) is emitted
            after `px-*` in Tailwind's order and silently wins the left side,
            which is exactly how the overlap survived the first fix.

            Smaller than PageHeader's 27px for the same reason — MEMBERSHIP is
            ten characters at 0.3em tracking, and at 27px it has nowhere to go
            once both gutters are reserved. */}
        <HeaderTitle
          className="font-display font-normal tracking-[0.3em] text-[#E8E2D6] pl-[0.3em] text-center mx-14"
          size="text-[22px] lg:text-[34px]"
          sizeScrolled="text-[18px] lg:text-[26px]"
        >
          {COPY.MEMBERSHIP_EYEBROW}
        </HeaderTitle>
      </div>
    )}
  >
    <div className="px-5 lg:px-12 pb-10 lg:pb-14">
      {children}
    </div>
  </AppLayout>
);

// One row of the billing table. Same proportions as Account's detail rows, on
// purpose — the two surfaces read as one system.
function BillingRow({ label, value, accent = false }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5 lg:py-4 border-b border-hairline-soft last:border-b-0">
      <span className="text-[11px] lg:text-xs tracking-[0.18em] text-bone-dim">{label}</span>
      <span
        className={`text-[15px] lg:text-[17px] text-right ${accent ? 'text-aurum-bright' : 'text-bone'}`}
      >
        {value}
      </span>
    </div>
  );
}

// The single most important sentence on the page, in serif, above the table.
//
// "ACCESS ENDS <date>" ONLY WHEN ACCESS ACTUALLY ENDS. A healthy trial gets
// "Trial ends" — on that date the trial converts and billing starts; access
// does not stop. Telling a converting member their access ends next Tuesday is
// wrong, and it is wrong in the direction that generates cancellations and
// support mail.
function statusHeadline(status, entitlement) {
  if (status === ENTITLEMENT_STATUS.PAST_DUE) return COPY.MEMBERSHIP_CARD_PAST_DUE_HEADLINE;

  // A trial ends on trial_end; a subscription on current_period_end. Different
  // fields, different instants — mixing them tells someone their access lasts
  // a month longer than it does.
  const isTrial = status === ENTITLEMENT_STATUS.TRIALING;
  const cancelling = Boolean(entitlement?.cancel_at_period_end);
  const end = formatDate(isTrial ? entitlement?.trial_end : entitlement?.current_period_end);

  if (!end) {
    return isTrial
      ? COPY.MEMBERSHIP_CARD_TRIAL_HEADLINE
      : COPY.MEMBERSHIP_CARD_ACTIVE_HEADLINE;
  }
  if (cancelling) return `${COPY.MEMBERSHIP_ENDS_PREFIX} ${end}`;
  if (isTrial) return `${COPY.MEMBERSHIP_TRIAL_ENDS_PREFIX} ${end}`;
  return `${COPY.MEMBERSHIP_RENEWS_PREFIX} ${end}`;
}

export default function Membership({ mode = 'status' }) {
  const navigate = useNavigate();
  const { entitlement, isPro, isLoading } = useEntitlement();
  const { currentUser } = useAuth() || {};
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [pollExpired, setPollExpired] = useState(false);

  const status = entitlement?.status || ENTITLEMENT_STATUS.NONE;

  // Conversion funnel: one view per visit, once the entitlement has resolved so
  // is_pro says whether this visit saw the paywall or the billing table. `mode`
  // separates organic /membership visits from Stripe return landings.
  useTrackViewOnce(
    CONVERSION_EVENTS.MEMBERSHIP_SCREEN_VIEWED,
    { mode, status, is_pro: isPro },
    !isLoading,
  );

  // Success-page poll. Stops the moment membership appears, and gives up at
  // POLL_WINDOW_MS so the page settles into a readable state either way.
  useEffect(() => {
    if (mode !== 'success' || isPro) return undefined;

    const getEntitlement = httpsCallable(functions, 'getEntitlement');
    const startedAt = Date.now();
    let cancelled = false;

    const timer = setInterval(async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > POLL_WINDOW_MS) {
        setPollExpired(true);
        clearInterval(timer);
        return;
      }
      try {
        // The result is discarded on purpose: useEntitlement's snapshot is the
        // source of truth. This call exists to prod a slow listener, not to
        // become a second source of state that can disagree with it.
        await getEntitlement({});
      } catch (err) {
        console.warn('Membership: entitlement poll failed', err);
      }
    }, POLL_INTERVAL_MS);

    return () => { cancelled = true; clearInterval(timer); };
  }, [mode, isPro]);

  const openPortal = useCallback(async () => {
    track(CONVERSION_EVENTS.MEMBERSHIP_MANAGE_CLICKED, { status });
    setPortalBusy(true);
    setPortalError(null);
    try {
      const createPortalSession = httpsCallable(functions, 'createPortalSession');
      // Same origin fix as checkout — the portal's return_url was pinned
      // to the server fallback too. See billingReturnUrls.
      const res = await createPortalSession(portalReturnUrls());
      const data = res?.data;
      if (data?.status === 'success' && data.portal_url) {
        window.location.assign(data.portal_url); // off-origin, not a router push
        return;
      }
      setPortalError(data?.error_message || COPY.MEMBERSHIP_ERROR);
      setPortalBusy(false);
    } catch (err) {
      console.error('Membership: portal failed', err);
      setPortalError(COPY.MEMBERSHIP_ERROR);
      setPortalBusy(false);
    }
  }, [status]);

  // ── Cancellation ──────────────────────────────────────────────────────────
  //
  // Our own flow rather than a trip through the Stripe portal. The portal can
  // cancel, but it is several taps down a Stripe-branded screen behind a button
  // labelled MANAGE — which is why this was reported as "no way to cancel".
  //
  // It also could not ask why, and the reason a member leaves is the most
  // useful thing they will ever tell us.
  //
  // No local success state: the callable does not write the entitlement (only
  // stripeWebhook does), but useEntitlement is a LIVE subscription, so the doc
  // updates on its own a moment later and StatusLine flips RENEWS to ENDS. The
  // modal just closes.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const confirmCancel = useCallback(async ({ reason, note }) => {
    setCancelBusy(true);
    setCancelError(null);
    try {
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      const res = await cancelSubscription({ reason, note });
      const data = res?.data;
      if (data?.status === 'success') {
        setCancelOpen(false);
        setCancelBusy(false);
        return;
      }
      setCancelError(data?.error_message || COPY.CANCEL_MODAL_ERROR);
      setCancelBusy(false);
    } catch (err) {
      console.error('Membership: cancel failed', err);
      setCancelError(COPY.CANCEL_MODAL_ERROR);
      setCancelBusy(false);
    }
  }, []);

  const goHome = useCallback(() => navigate(ROUTES.HOME), [navigate]);

  // ── /membership/cancel ────────────────────────────────────────────────────
  if (mode === 'cancel') {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-6 py-16 text-center">
          <CensorBar mode="LABEL" value="NOT COMPLETED" />
          <h1 className="font-display text-3xl text-bone">{COPY.MEMBERSHIP_CANCEL_HEADLINE}</h1>
          <p className="font-display text-bone-dim max-w-sm">{COPY.MEMBERSHIP_CANCEL_BODY}</p>
          <BoneButton onClick={goHome}>{COPY.MEMBERSHIP_RETURN_CTA}</BoneButton>
        </div>
      </Shell>
    );
  }

  // ── /membership/success ───────────────────────────────────────────────────
  //
  // ── NO CENSOR BAR ON THIS SCREEN, DELIBERATELY ────────────────────────────
  //
  // Both states below used to open with a [LABEL] bar — the same black box the
  // CANCEL branch above still carries. That made "you just paid" and "you just
  // decided not to" render identically, on the one screen in the product where
  // the difference is the whole point.
  //
  // So the confirmed state gets a drawn aurum seal and the pending state gets
  // the spinner it always should have been (it is, literally, waiting on the
  // webhook). The bars on /membership/cancel and /membership STAY — this is
  // scoped to the success landing, and the split is intentional rather than a
  // half-finished migration. Do not "tidy" it in either direction.
  if (mode === 'success') {
    if (isPro) {
      return (
        <Shell>
          <div className="flex flex-col items-center gap-6 py-16 text-center" data-testid="membership-success">
            <MembershipCelebration />
            <h1 className="font-display text-3xl text-bone">{COPY.MEMBERSHIP_SUCCESS_HEADLINE}</h1>
            <p className="font-display text-bone-dim max-w-sm">{COPY.MEMBERSHIP_SUCCESS_BODY}</p>
            <AurumButton onClick={goHome}>{COPY.MEMBERSHIP_RETURN_CTA}</AurumButton>
          </div>
        </Shell>
      );
    }
    return (
      <Shell>
        <div className="flex flex-col items-center gap-6 py-16 text-center" data-testid="membership-processing">
          {/* No `label` — the h1 under it already says CONFIRMING, and the
              spinner's own role="status" would otherwise announce the wait
              twice. That headline stays CONFIRMING even once the poll gives
              up, which is still true: the webhook may yet land. */}
          <LogoSpinner size={96} />
          <h1 className="font-display text-3xl text-bone">{COPY.MEMBERSHIP_PROCESSING_HEADLINE}</h1>
          <p className="font-display text-bone-dim max-w-sm">
            {pollExpired ? COPY.MEMBERSHIP_PROCESSING_SLOW : COPY.MEMBERSHIP_PROCESSING_BODY}
          </p>
        </div>
      </Shell>
    );
  }

  // ── /membership ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Shell>
        <div className="w-full min-h-[240px] bg-vein/40 animate-pulse" aria-busy="true" />
      </Shell>
    );
  }

  const isPastDue = status === ENTITLEMENT_STATUS.PAST_DUE;
  // Written by stripeWebhook from customer.subscription.updated, so it flips on
  // its own a moment after the cancel callable returns.
  const alreadyCancelling = Boolean(entitlement?.cancel_at_period_end);
  const statusLabel = {
    [ENTITLEMENT_STATUS.ACTIVE]: COPY.MEMBERSHIP_STATUS_ACTIVE,
    [ENTITLEMENT_STATUS.TRIALING]: COPY.MEMBERSHIP_STATUS_TRIAL,
    [ENTITLEMENT_STATUS.PAST_DUE]: COPY.MEMBERSHIP_STATUS_PAST_DUE,
  }[status];

  // Not a member and not recoverable — the paywall IS the page.
  if (!isPro && !isPastDue) {
    return (
      <Shell>
        <MembershipCTA reason="scan_unlock" />
      </Shell>
    );
  }

  const isTrialing = status === ENTITLEMENT_STATUS.TRIALING;
  const trialEnd = formatMembershipDate(entitlement?.trial_end);
  const periodEnd = formatMembershipDate(entitlement?.current_period_end);
  // The account, not the subscription — this row is the one that says the
  // person predates what they are paying for.
  const memberSince = formatMemberSince(currentUser?.metadata?.creationTime);

  return (
    <Shell>
      <div className="mx-auto w-full max-w-[560px]" data-testid="membership-status">
        <div className="flex flex-col items-center gap-5 pt-6 pb-8 text-center">
          <CensorBar mode="LABEL" value={statusLabel} />
          <h2 className="font-display text-[26px] lg:text-[30px] leading-tight text-bone m-0">
            {statusHeadline(status, entitlement)}
          </h2>
          {/* Renders nothing outside a trial, so it needs no guard. */}
          <TrialBadge pill />
          {isPastDue && (
            <p className="font-display text-bone-dim max-w-sm m-0">{COPY.MEMBERSHIP_PAST_DUE_BODY}</p>
          )}
        </div>

        {/* ── Billing ──────────────────────────────────────────────────────
            Every number, once, labelled. Account's card is the summary and
            the door; this is the detail it stopped repeating.

            There is deliberately no card-on-file row. The entitlement doc
            holds no brand or last4 (FIRESTORE-SCHEMA.md `entitlements/{uid}`),
            and a payment method is not something to approximate — MANAGE
            MEMBERSHIP opens the Stripe portal, where the real one lives. */}
        <div className="text-[11px] lg:text-xs tracking-[0.22em] text-bone-dim">
          {COPY.MEMBERSHIP_BILLING_EYEBROW}
        </div>
        <div className="mt-2">
          <BillingRow label={COPY.MEMBERSHIP_ROW_PLAN} value={COPY.MEMBERSHIP_PLAN_NAME} />
          <BillingRow label={COPY.MEMBERSHIP_ROW_PRICE} value={COPY.MEMBERSHIP_PRICE_MONTHLY} />

          {isTrialing && trialEnd && (
            <BillingRow label={COPY.MEMBERSHIP_ROW_TRIAL_ENDS} value={trialEnd} accent />
          )}
          {/* "None" rather than a hidden row. Someone who has just cancelled
              came back to confirm exactly this, and an absent row confirms
              nothing. */}
          {isTrialing && (
            <BillingRow
              label={COPY.MEMBERSHIP_ROW_FIRST_CHARGE}
              value={alreadyCancelling
                ? COPY.MEMBERSHIP_NO_FURTHER_CHARGE
                : (trialEnd || COPY.MEMBERSHIP_NO_FURTHER_CHARGE)}
            />
          )}
          {!isTrialing && periodEnd && (
            <BillingRow
              label={alreadyCancelling
                ? COPY.MEMBERSHIP_ROW_ACCESS_ENDS
                : COPY.MEMBERSHIP_ROW_RENEWS}
              value={periodEnd}
            />
          )}
          {memberSince && (
            <BillingRow label={COPY.MEMBERSHIP_ROW_MEMBER_SINCE} value={memberSince} />
          )}
        </div>

        {/* ── Included ─────────────────────────────────────────────────────
            The same four lines the paywall sells, shown to the person who
            bought them. Not a second sales pitch — a receipt. */}
        <div className="mt-7 rounded-card border border-hairline-soft bg-[#161615] p-5 lg:p-6">
          <div className="text-[11px] lg:text-xs tracking-[0.22em] text-aurum-bright">
            {COPY.MEMBERSHIP_INCLUDED_EYEBROW}
          </div>
          <ul className="mt-4 flex flex-col gap-3.5 list-none p-0 m-0">
            {COPY.MEMBERSHIP_BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-3">
                <LuCheck size={16} className="text-aurum shrink-0 mt-1" aria-hidden="true" />
                <span className="text-[15px] lg:text-base text-bone">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-7 flex flex-col items-center gap-4">
          <AurumButton
            onClick={openPortal}
            loading={portalBusy}
            fullWidth
            data-testid="membership-portal"
          >
            {isPastDue ? COPY.MEMBERSHIP_UPDATE_PAYMENT_CTA : COPY.MEMBERSHIP_MANAGE_CTA}
          </AurumButton>

          {/* Cancelling is its own control, not a thing to go hunting for
              inside Stripe's portal. MANAGE keeps cards and invoices; this
              handles leaving.

              Hidden once the cancellation is scheduled — the headline already
              says ACCESS ENDS <date>, and a live cancel button under it would
              read as though the first press had not worked. Also hidden for
              past_due, where the useful action is fixing the card. */}
          {!isPastDue && !alreadyCancelling && (
            <GhostButton
              onClick={() => {
                track(CONVERSION_EVENTS.MEMBERSHIP_CANCEL_CLICKED, { status });
                setCancelOpen(true);
              }}
              data-testid="membership-cancel"
            >
              {COPY.MEMBERSHIP_CANCEL_CTA}
            </GhostButton>
          )}

          {alreadyCancelling && (
            <p className="font-display text-bone-dim text-sm text-center m-0" data-testid="membership-cancel-scheduled">
              {COPY.MEMBERSHIP_CANCEL_SCHEDULED}
            </p>
          )}

          {/* Bone, never red — DESIGN.md forbids red even for errors. */}
          {portalError && (
            <p className="font-display text-bone text-sm text-center m-0" role="alert">{portalError}</p>
          )}
        </div>

        {/* The merchant-of-record disclosure is the one place museum-text would
            cost real money: an unrecognised LINK.COM line is the biggest
            driver of avoidable chargebacks, and chargebacks threaten Managed
            Payments eligibility itself. It stays plain, and it stays visible
            to existing members, not just at checkout. */}
        <div className="mt-8 flex flex-col gap-2 text-center">
          {isTrialing && !alreadyCancelling && (
            <p className="font-display text-[13px] text-bone-dim m-0">
              {COPY.MEMBERSHIP_TRIAL_NOTE}
            </p>
          )}
          <p className="font-display text-[13px] text-bone-dim leading-relaxed m-0">
            {COPY.MEMBERSHIP_MOR_NOTE}
          </p>
        </div>
      </div>

      <CancelMembershipModal
        open={cancelOpen}
        onConfirm={confirmCancel}
        onDismiss={() => {
 setCancelOpen(false); setCancelError(null);
}}
        submitting={cancelBusy}
        error={cancelError}
        periodEndLabel={formatDate(entitlement?.current_period_end)}
      />
    </Shell>
  );
}
